import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from '../utils/axios';
import toast from 'react-hot-toast';
import {
  Plus, FolderOpen, Receipt, Search, X, ChevronDown, Calendar, DollarSign, FileText, User, Loader2, Edit, Trash2, Save, Eye
} from 'lucide-react';
import FilterDropdown from '../components/FilterDropdown';

// ─── Styles ──────────────────────────────────────────────
const styles = {
  page: { fontFamily: "'Inter','Segoe UI',sans-serif", minHeight: '100vh' },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    flexWrap: 'wrap', gap: 12, marginBottom: 24
  },
  title: { fontSize: 22, fontWeight: 700, color: '#1e293b', margin: 0 },
  subtitle: { fontSize: 13, color: '#64748b', margin: 0 },
  tabs: {
    display: 'flex', gap: 4, background: '#f1f5f9', borderRadius: 10, padding: 4
  },
  tab: (active) => ({
    padding: '10px 22px', borderRadius: 8, border: 'none', cursor: 'pointer',
    fontWeight: 600, fontSize: 13, transition: 'all .2s',
    background: active ? '#0A5F38' : 'transparent',
    color: active ? '#fff' : '#64748b',
    boxShadow: active ? '0 2px 8px rgba(10,95,56,.25)' : 'none'
  }),
  card: {
    background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
    boxShadow: '0 1px 3px rgba(0,0,0,.06)', overflow: 'hidden'
  },
  cardHeader: {
    padding: '16px 20px', borderBottom: '1px solid #f1f5f9',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8
  },
  cardTitle: { fontSize: 15, fontWeight: 600, color: '#334155', margin: 0 },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    padding: '12px 16px', textAlign: 'center', fontSize: 11, fontWeight: 600,
    color: '#64748b', textTransform: 'uppercase', letterSpacing: '.5px',
    background: '#f8fafc', borderBottom: '1px solid #e2e8f0'
  },
  td: {
    padding: '12px 16px', fontSize: 13, color: '#334155', borderBottom: '1px solid #f1f5f9',
    textAlign: 'center'
  },
  btn: (variant = 'primary') => ({
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px',
    borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13,
    transition: 'all .2s',
    background: variant === 'primary' ? '#0A5F38' : variant === 'danger' ? '#ef4444' : '#f1f5f9',
    color: variant === 'primary' ? '#fff' : variant === 'danger' ? '#fff' : '#334155'
  }),
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 1000,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
  },
  modal: {
    background: '#fff', borderRadius: 16, width: '100%', maxWidth: 520,
    boxShadow: '0 20px 60px rgba(0,0,0,.2)', maxHeight: '90vh', overflowY: 'auto'
  },
  modalHeader: {
    padding: '20px 24px', borderBottom: '1px solid #e2e8f0',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between'
  },
  modalTitle: { fontSize: 17, fontWeight: 700, color: '#1e293b', margin: 0 },
  modalBody: { padding: 24 },
  modalFooter: {
    padding: '16px 24px', borderTop: '1px solid #e2e8f0',
    display: 'flex', justifyContent: 'flex-end', gap: 8
  },
  field: { marginBottom: 16 },
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6 },
  input: {
    width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1',
    fontSize: 13, outline: 'none', transition: 'border .2s', boxSizing: 'border-box'
  },
  select: {
    width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1',
    fontSize: 13, outline: 'none', background: '#fff', boxSizing: 'border-box',
    appearance: 'none', cursor: 'pointer'
  },
  textarea: {
    width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1',
    fontSize: 13, outline: 'none', resize: 'vertical', minHeight: 60, fontFamily: 'inherit',
    boxSizing: 'border-box'
  },
  badge: (color) => ({
    display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 11,
    fontWeight: 600,
    background: color === 'green' ? '#dcfce7' : color === 'blue' ? '#dbeafe' : '#f1f5f9',
    color: color === 'green' ? '#166534' : color === 'blue' ? '#1e40af' : '#475569'
  }),
  statusBadge: (status) => {
    const colors = {
      pending: { bg: '#fef3c7', text: '#d97706' },
      approved: { bg: '#dcfce7', text: '#16a34a' },
      rejected: { bg: '#fee2e2', text: '#dc2626' }
    };
    const c = colors[status] || colors.pending;
    return {
      display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 11,
      fontWeight: 700, background: c.bg, color: c.text, textTransform: 'uppercase'
    };
  },
  summaryCard: {
    background: 'linear-gradient(135deg, #0A5F38 0%, #0d7a4a 100%)',
    borderRadius: 12, padding: '16px 20px', color: '#fff', minWidth: 140
  },
  empty: {
    textAlign: 'center', padding: '48px 20px', color: '#94a3b8'
  },
  searchWrap: {
    position: 'relative', maxWidth: 260
  },
  searchInput: {
    width: '100%', padding: '8px 12px 8px 34px', borderRadius: 8,
    border: '1px solid #e2e8f0', fontSize: 13, outline: 'none', boxSizing: 'border-box'
  },
  searchIcon: { position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' },
  docDropdown: {
    position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff',
    border: '1px solid #e2e8f0', borderRadius: 8, maxHeight: 200, overflowY: 'auto',
    zIndex: 10, boxShadow: '0 8px 24px rgba(0,0,0,.12)'
  },
  docItem: {
    padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f8fafc',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center'
  }
};

const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// ─── Component ──────────────────────────────────────────
const EmployeeExpenses = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedProject, setSelectedProject] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projectsTotal, setProjectsTotal] = useState(0);
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState({ totalAmount: 0, count: 0, approvedAmount: 0, pendingAmount: 0, employeeCount: 0 });
  const [linkedDocs, setLinkedDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');

  const handleSort = (key) => {
    let order = 'asc';
    if (sortBy === key && sortOrder === 'asc') {
      order = 'desc';
    }
    setSortBy(key);
    setSortOrder(order);
  };

  const handleDocumentClick = (doc) => {
    if (!doc.number) return;
    const num = doc.number;
    if (num.startsWith('INV')) {
      navigate(`/sales/invoices?view=${num}`);
    } else if (num.startsWith('EST') || num.startsWith('QTN')) {
      navigate(`/sales/estimates?view=${num}`);
    } else if (num.startsWith('CO') || /^\d+$/.test(num)) {
      navigate(`/sales/closed-orders?view=${num}`);
    }
  };

  // Modal state
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedExpenseForView, setSelectedExpenseForView] = useState(null);
  const [saving, setSaving] = useState(false);

  // Pagination states
  const [projectsPage, setProjectsPage] = useState(1);
  const [projectsTotalPages, setProjectsTotalPages] = useState(1);
  const [expensesPage, setExpensesPage] = useState(1);
  const [expensesTotalPages, setExpensesTotalPages] = useState(1);

  // Project form
  const [pName, setPName] = useState('');
  const [pDocId, setPDocId] = useState(null);
  const [pDocNumber, setPDocNumber] = useState('');
  const [pDocType, setPDocType] = useState(null);
  const [pDocCustomer, setPDocCustomer] = useState('');
  const [pLinkedDocs, setPLinkedDocs] = useState([]);
  const [docSearch, setDocSearch] = useState('');
  const [showDocDropdown, setShowDocDropdown] = useState(false);

  // Expense form
  const [eProjectId, setEProjectId] = useState('');
  const [eDate, setEDate] = useState(new Date().toISOString().slice(0, 10));
  const [eItems, setEItems] = useState([
    { amount: '', expenseDescription: '', notes: '' },
    { amount: '', expenseDescription: '', notes: '' },
    { amount: '', expenseDescription: '', notes: '' }
  ]);

  // ─── Fetch data ──────────────────────────────────────
  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`/expenses/projects?page=${projectsPage}&limit=10`);
      setProjects(data.projects || []);
      setProjectsTotalPages(data.totalPages || 1);
      setProjectsTotal(data.totalItems || 0);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load projects');
    } finally { setLoading(false); }
  }, [projectsPage]);

  const fetchExpenses = useCallback(async () => {
    try {
      setLoading(true);
      let url = `/expenses?page=${expensesPage}&limit=10`;
      if (selectedProject) {
        url += `&projectId=${selectedProject._id}`;
      }
      const { data } = await axios.get(url);
      setExpenses(data.expenses || []);
      setExpensesTotalPages(data.totalPages || 1);
      setSummary(data.summary || { totalAmount: 0, count: 0, approvedAmount: 0, pendingAmount: 0, employeeCount: 0 });
    } catch (err) {
      console.error(err);
      toast.error('Failed to load expenses');
    } finally { setLoading(false); }
  }, [expensesPage, selectedProject]);

  const fetchLinkedDocs = useCallback(async () => {
    try {
      const { data } = await axios.get('/expenses/linked-documents');
      setLinkedDocs(data.documents || []);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => {
    fetchProjects();
    fetchExpenses();
    fetchLinkedDocs();
  }, [fetchProjects, fetchExpenses, fetchLinkedDocs]);

  useEffect(() => {
    setProjectsPage(1);
    setExpensesPage(1);
  }, [search]);

  useEffect(() => {
    setExpensesPage(1);
  }, [selectedProject]);

  // ─── Project modal handlers ─────────────────────────
  const openProjectModal = (p = null) => {
    if (p) {
      setPName(p.projectName || '');
      setPLinkedDocs(p.linkedDocuments || []);
      setEditingProject(p);
    } else {
      setPName(''); setPDocId(null); setPDocNumber(''); setPDocType(null); setPDocCustomer(''); setPLinkedDocs([]); setDocSearch('');
      setEditingProject(null);
    }
    setShowProjectModal(true);
  };

  const handleCreateProject = async () => {
    if (!pName.trim()) { toast.error('Project name is required'); return; }
    try {
      setSaving(true);
      if (editingProject) {
        await axios.put(`/expenses/projects/${editingProject._id}`, {
          projectName: pName.trim(),
          linkedDocuments: pLinkedDocs
        });
        toast.success('Project updated!');
      } else {
        await axios.post('/expenses/projects', {
          projectName: pName.trim(),
          linkedDocuments: pLinkedDocs
        });
        toast.success('Project created!');
      }
      setShowProjectModal(false);
      fetchProjects();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save project');
    } finally { setSaving(false); }
  };

  // ─── Expense modal handlers ─────────────────────────
  const openViewModal = (e) => {
    setSelectedExpenseForView(e);
    setShowViewModal(true);
  };

  const openExpenseModal = (e = null) => {
    setEditingItem(e);
    const formatDate = (d) => {
      const dt = new Date(d);
      return isNaN(dt.getTime()) ? new Date().toISOString().slice(0, 10) : dt.toISOString().slice(0, 10);
    };
    if (e) {
      setEProjectId(e.projectId?._id || e.projectId);
      setEDate(formatDate(e.date));
      setEItems([{ amount: e.amount, expenseDescription: e.expenseDescription, notes: e.notes || '' }]);
    } else {
      setEProjectId(selectedProject?._id || '');
      setEDate(formatDate(new Date()));
      setEItems([
        { amount: '', expenseDescription: '', notes: '' },
        { amount: '', expenseDescription: '', notes: '' },
        { amount: '', expenseDescription: '', notes: '' }
      ]);
    }
    setShowExpenseModal(true);
  };

  const handleCreateExpense = async () => {
    if (!eProjectId) { toast.error('Select a project'); return; }
    if (!eDate) { toast.error('Date is required'); return; }

    try {
      setSaving(true);
      if (editingItem) {
        const payload = {
          projectId: eProjectId,
          date: eDate,
          amount: parseFloat(eItems[0].amount),
          expenseDescription: eItems[0].expenseDescription,
          notes: eItems[0].notes
        };
        await axios.put(`/expenses/${editingItem._id}`, payload);
        toast.success('Expense updated!');
      } else {
        const validItems = eItems.filter(it => it.amount && parseFloat(it.amount) > 0 && it.expenseDescription?.trim());
        if (validItems.length === 0) { toast.error('Add at least one valid expense'); return; }

        await axios.post('/expenses', {
          projectId: eProjectId,
          date: eDate,
          items: validItems
        });
        toast.success(`${validItems.length} Expenses added!`);
      }
      setShowExpenseModal(false);
      fetchExpenses();
      fetchProjects();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save expense');
    } finally { setSaving(false); }
  };

  const handleApproveExpense = async (id) => {
    try {
      setSaving(true);
      await axios.put(`/expenses/${id}/approve`);
      toast.success('Expense approved!');
      setShowViewModal(false);
      fetchExpenses();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to approve expense');
    } finally { setSaving(false); }
  };

  const handleDeleteExpense = async (id) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;
    try {
      setSaving(true);
      await axios.delete(`/expenses/${id}`);
      toast.success('Expense deleted!');
      setShowViewModal(false);
      fetchExpenses();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete expense');
    } finally { setSaving(false); }
  };

  const handleEditFromView = (expense) => {
    setShowViewModal(false);
    openExpenseModal(expense);
  };

  // ─── Filtered data ──────────────────────────────────
  const filteredProjects = projects.filter(p =>
    p.projectName?.toLowerCase().includes(search.toLowerCase()) ||
    p.linkedDocumentNumber?.toLowerCase().includes(search.toLowerCase())
  );

  const filteredExpenses = React.useMemo(() => {
    let result = expenses.filter(e => {
      const matchProject = !selectedProject || 
                           e.projectId?._id === selectedProject._id || 
                           e.projectId === selectedProject._id ||
                           e.projectName === selectedProject.projectName;
      
      const matchSearch = e.projectName?.toLowerCase().includes(search.toLowerCase()) ||
                          e.employeeName?.toLowerCase().includes(search.toLowerCase()) ||
                          e.expenseDescription?.toLowerCase().includes(search.toLowerCase()) ||
                          e.linkedDocumentNumber?.toLowerCase().includes(search.toLowerCase()) ||
                          e.status?.toLowerCase().includes(search.toLowerCase()) ||
                          e.amount?.toString().includes(search);
                          
      return matchProject && matchSearch;
    });

    if (sortBy) {
      result.sort((a, b) => {
        let valA = a[sortBy];
        let valB = b[sortBy];

        if (sortBy === 'date') {
          valA = new Date(valA);
          valB = new Date(valB);
        }

        if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
        if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    } else {
      result.sort((a, b) => new Date(b.date) - new Date(a.date));
    }

    return result;
  }, [expenses, search, sortBy, sortOrder]);

  const filteredDocs = linkedDocs.filter(d =>
    d.number?.toLowerCase().includes(docSearch.toLowerCase()) ||
    d.customerName?.toLowerCase().includes(docSearch.toLowerCase())
  );

  // ─── Render ─────────────────────────────────────────
  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>💸 Expenses</h1>
          <p style={styles.subtitle}>Manage projects and track expenses</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <div style={styles.summaryCard}>
            <div style={{ fontSize: 11, opacity: .8 }}>{selectedProject ? 'Project Spent' : 'Total Projects'}</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{selectedProject ? fmt(summary.totalAmount) : projectsTotal}</div>
          </div>
          <div style={{ ...styles.summaryCard, background: 'linear-gradient(135deg, #C5A57D 0%, #b8956d 100%)' }}>
            <div style={{ fontSize: 11, opacity: .9, color: '#1e293b' }}>{selectedProject ? 'Entries' : 'Total Expenses'}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#1e293b' }}>{selectedProject ? summary.count : fmt(summary.totalAmount)}</div>
          </div>
        </div>
      </div>

      {/* Tabs + Action */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div></div>
        <div style={{ display: 'flex', gap: 8 }}>
          {!selectedProject ? (
            <button style={styles.btn('primary')} onClick={() => openProjectModal()}><Plus size={14} /> New Project</button>
          ) : (
            <button style={styles.btn('primary')} onClick={() => openExpenseModal()}><Plus size={14} /> Add Expense</button>
          )}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div style={styles.empty}><Loader2 size={28} className="animate-spin" style={{ margin: '0 auto 8px' }} /> Loading…</div>
      ) : !selectedProject ? (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <h3 style={styles.cardTitle}>All Projects ({filteredProjects.length})</h3>
              <div style={{ position: 'relative', width: 250 }}>
                <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                <input
                  style={{ width: '100%', padding: '8px 12px 8px 34px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, background: '#f8fafc' }}
                  placeholder="Search projects..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
            </div>
          </div>
          {filteredProjects.length === 0 ? (
            <div style={styles.empty}>
              <FolderOpen size={36} style={{ margin: '0 auto 8px', opacity: .4 }} />
              <p style={{ fontWeight: 500, marginBottom: 4 }}>No projects yet</p>
              <p style={{ fontSize: 12 }}>Create a project to start tracking expenses</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>#</th>
                    <th style={styles.th}>Project Name</th>
                    <th style={styles.th}>Linked Document</th>
                    <th style={styles.th}>Created By</th>
                    <th style={styles.th}>Created Date</th>
                    <th style={styles.th}>Entries</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProjects.map((p, i) => (
                    <tr key={p._id} style={{ transition: 'background .15s' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}>
                      <td style={styles.td}>{i + 1}</td>
                      <td style={{ ...styles.td, fontWeight: 600, color: '#0A5F38', cursor: 'pointer' }} onClick={() => setSelectedProject(p)}>
                        {p.projectName}
                      </td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
                          {p.linkedDocuments && p.linkedDocuments.length > 0 ? p.linkedDocuments.map((doc, idx) => (
                            <span 
                              key={idx} 
                              style={{ ...styles.badge(doc.docType || doc.type), cursor: 'pointer' }}
                              onClick={() => handleDocumentClick(doc)}
                            >
                              {doc.number}
                            </span>
                          )) : p.linkedDocumentNumber ? (
                            <span 
                              style={{ ...styles.badge(p.linkedDocumentType === 'invoice' ? 'blue' : 'green'), cursor: 'pointer' }}
                              onClick={() => handleDocumentClick({ number: p.linkedDocumentNumber })}
                            >
                              {p.linkedDocumentNumber}
                            </span>
                          ) : '—'}
                        </div>
                      </td>
                      <td style={styles.td}>{p.createdByName || '—'}</td>
                      <td style={styles.td}>{fmtDate(p.createdAt)}</td>
                      <td style={styles.td}>{p.expenseCount || 0}</td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                          <button 
                            style={{ ...styles.btn('outline'), padding: '4px 8px', fontSize: 12, display: 'flex', alignItems: 'center' }} 
                            onClick={() => openProjectModal(p)}
                          >
                            <Edit size={12} style={{ marginRight: 4 }} /> Edit
                          </button>
                          <button 
                            style={{ ...styles.btn('primary'), padding: '4px 8px', fontSize: 12 }} 
                            onClick={() => setSelectedProject(p)}
                          >
                            View Expenses
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {/* Projects Pagination Controls */}
          <div style={{ padding: '12px 20px', borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <span style={{ fontSize: 13, color: '#64748b' }}>
              Showing Page <b>{projectsPage}</b> of <b>{projectsTotalPages}</b>
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button 
                style={styles.btn('outline')} 
                disabled={projectsPage === 1}
                onClick={() => setProjectsPage(p => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <button 
                style={styles.btn('outline')} 
                disabled={projectsPage >= projectsTotalPages}
                onClick={() => setProjectsPage(p => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button 
                onClick={() => setSelectedProject(null)} 
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: 6, transition: 'background .2s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <ChevronDown style={{ transform: 'rotate(90deg)' }} size={16} /> Back
              </button>
              <h3 style={styles.cardTitle}>Project: {selectedProject?.projectName}</h3>
              <div style={{ position: 'relative', width: 250 }}>
                <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                <input
                  style={{ width: '100%', padding: '8px 12px 8px 34px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, background: '#f8fafc' }}
                  placeholder="Search expenses..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
            </div>
          </div>
          {filteredExpenses.length === 0 ? (
            <div style={styles.empty}>
              <Receipt size={36} style={{ margin: '0 auto 8px', opacity: .4 }} />
              <p style={{ fontWeight: 500, marginBottom: 4 }}>No expenses yet</p>
              <p style={{ fontSize: 12 }}>Add an expense to a project</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>#</th>
                    <th style={styles.th}>Linked Doc</th>
                    <th style={{ ...styles.th, cursor: 'pointer' }} onClick={() => handleSort('date')}>
                      Date {sortBy === 'date' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th style={{ ...styles.th, cursor: 'pointer' }} onClick={() => handleSort('amount')}>
                      Amount {sortBy === 'amount' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th style={styles.th}>Description</th>
                    <th style={styles.th}>Created By</th>
                    <th style={{ ...styles.th, cursor: 'pointer' }} onClick={() => handleSort('status')}>
                      Status {sortBy === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredExpenses.map((e, i) => (
                    <tr key={e._id} style={{ transition: 'background .15s' }}
                      onMouseEnter={ev => ev.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={ev => ev.currentTarget.style.background = ''}>
                      <td style={styles.td}>{i + 1}</td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
                          {e.linkedDocumentNumber ? (
                            <span 
                              style={{ ...styles.badge(e.linkedDocumentType === 'invoice' ? 'blue' : 'green'), cursor: 'pointer' }}
                              onClick={() => handleDocumentClick({ number: e.linkedDocumentNumber })}
                            >
                              {e.linkedDocumentNumber}
                            </span>
                          ) : e.projectId?.linkedDocuments && e.projectId.linkedDocuments.length > 0 ? e.projectId.linkedDocuments.map((doc, idx) => (
                            <span 
                              key={idx} 
                              style={{ ...styles.badge(doc.docType || doc.type), cursor: 'pointer' }}
                              onClick={() => handleDocumentClick(doc)}
                            >
                              {doc.number}
                            </span>
                          )) : e.projectId?.linkedDocumentNumber ? (
                            <span 
                              style={{ ...styles.badge(e.projectId.linkedDocumentType === 'invoice' ? 'blue' : 'green'), cursor: 'pointer' }}
                              onClick={() => handleDocumentClick({ number: e.projectId.linkedDocumentNumber })}
                            >
                              {e.projectId.linkedDocumentNumber}
                            </span>
                          ) : '—'}
                        </div>
                      </td>
                      <td style={styles.td}>{fmtDate(e.date)}</td>
                      <td style={{ ...styles.td, fontWeight: 600, color: '#0A5F38' }}>{fmt(e.amount)}</td>
                      <td style={{ ...styles.td, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.expenseDescription}</td>
                      <td style={styles.td}>{e.employeeName || 'Unknown'}</td>
                      <td style={styles.td}><span style={styles.statusBadge(e.status || 'pending')}>{e.status || 'pending'}</span></td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                          <button
                            onClick={() => openViewModal(e)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#0A5F38', padding: '4px' }}
                            title="View"
                          >
                            <Eye size={16} />
                          </button>
                          {(e.status === 'pending' || !e.status) && (user?.role === 'admin' || e.employeeId === user?.id || e.employeeId === user?._id) && (
                            <button
                              onClick={() => openExpenseModal(e)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#0A5F38', padding: '4px' }}
                              title="Edit"
                            >
                              <Edit size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {/* Expenses Pagination Controls */}
          <div style={{ padding: '12px 20px', borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <span style={{ fontSize: 13, color: '#64748b' }}>
              Showing Page <b>{expensesPage}</b> of <b>{expensesTotalPages}</b>
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button 
                style={styles.btn('outline')} 
                disabled={expensesPage === 1}
                onClick={() => setExpensesPage(p => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <button 
                style={styles.btn('outline')} 
                disabled={expensesPage >= expensesTotalPages}
                onClick={() => setExpensesPage(p => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── View Expense Modal ──────────────────────── */}
      {showViewModal && selectedExpenseForView && (
        <div style={styles.overlay} onClick={() => setShowViewModal(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}><Receipt size={18} style={{ verticalAlign: -3, marginRight: 6 }} /> Expense Details</h2>
              <button onClick={() => setShowViewModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><X size={18} color="#94a3b8" /></button>
            </div>
            <div style={styles.modalBody}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Project Name</label>
                  <div style={{ fontSize: 14, color: '#1e293b', marginTop: 4 }}>{selectedExpenseForView.projectName}</div>
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Date</label>
                  <div style={{ fontSize: 14, color: '#1e293b', marginTop: 4 }}>{fmtDate(selectedExpenseForView.date)}</div>
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Amount</label>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#0A5F38', marginTop: 4 }}>{fmt(selectedExpenseForView.amount)}</div>
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Status</label>
                  <div style={{ marginTop: 4 }}><span style={styles.statusBadge(selectedExpenseForView.status || 'pending')}>{selectedExpenseForView.status || 'pending'}</span></div>
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Description</label>
                  <div style={{ fontSize: 14, color: '#1e293b', marginTop: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{selectedExpenseForView.expenseDescription}</div>
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Notes</label>
                  <div style={{ fontSize: 14, color: '#1e293b', marginTop: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{selectedExpenseForView.notes || '—'}</div>
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Linked Document</label>
                  <div style={{ marginTop: 4 }}>
                    {selectedExpenseForView.linkedDocumentNumber ? (
                      <span 
                        style={{ ...styles.badge(selectedExpenseForView.linkedDocumentType === 'invoice' ? 'blue' : 'green'), cursor: 'pointer' }}
                        onClick={() => handleDocumentClick({ number: selectedExpenseForView.linkedDocumentNumber })}
                      >
                        {selectedExpenseForView.linkedDocumentNumber}
                      </span>
                    ) : '—'}
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Created By</label>
                  <div style={{ fontSize: 14, color: '#1e293b', marginTop: 4 }}>{selectedExpenseForView.employeeName || 'Unknown'}</div>
                </div>
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button onClick={() => setShowViewModal(false)} style={styles.btn('secondary')}>Close</button>
              <div style={{ display: 'flex', gap: 8 }}>
                {user?.role === 'admin' && (
                  <>
                    <button onClick={() => handleDeleteExpense(selectedExpenseForView._id)} style={{ ...styles.btn('outline'), color: '#dc2626', borderColor: '#dc2626' }}>Delete</button>
                    {selectedExpenseForView.status === 'pending' && (
                      <button onClick={() => handleApproveExpense(selectedExpenseForView._id)} style={styles.btn('primary')}>Approve</button>
                    )}
                  </>
                )}
                {((user?.role !== 'admin' && selectedExpenseForView.status === 'pending' && (selectedExpenseForView.employeeId === user?.id || selectedExpenseForView.employeeId === user?._id)) || user?.role === 'admin') && (
                  <button onClick={() => handleEditFromView(selectedExpenseForView)} style={styles.btn('primary')}>Edit</button>
                )}
                {user?.role !== 'admin' && selectedExpenseForView.status === 'pending' && (selectedExpenseForView.employeeId === user?.id || selectedExpenseForView.employeeId === user?._id) && (
                  <button onClick={() => handleDeleteExpense(selectedExpenseForView._id)} style={{ ...styles.btn('outline'), color: '#dc2626', borderColor: '#dc2626' }}>Delete</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── New Project Modal ──────────────────────── */}
      {showProjectModal && (
        <div style={styles.overlay} onClick={() => setShowProjectModal(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}><FolderOpen size={18} style={{ verticalAlign: -3, marginRight: 6 }} /> {editingProject ? 'Edit Project' : 'New Project'}</h2>
              <button onClick={() => setShowProjectModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><X size={18} color="#94a3b8" /></button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.field}>
                <label style={styles.label}>Project Name *</label>
                <input style={styles.input} placeholder="Enter project name" value={pName} onChange={e => setPName(e.target.value)} autoFocus />
              </div>
              <div style={{ ...styles.field, position: 'relative' }}>
                <label style={styles.label}>Closed Order / Invoice Number</label>
                <input
                  style={styles.input}
                  placeholder="Search closed orders or invoices…"
                  value={docSearch}
                  onChange={e => { setDocSearch(e.target.value); setShowDocDropdown(true); }}
                  onFocus={() => setShowDocDropdown(true)}
                />

                {/* Selected Documents List */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                  {pLinkedDocs.map((doc, idx) => (
                    <div key={idx} style={{ background: '#f8fafc', padding: '4px 10px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, border: '1px solid #e2e8f0' }}>
                      <span style={styles.badge(doc.docType === 'invoice' ? 'blue' : 'green')}>{doc.number}</span>
                      <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }} onClick={() => setPLinkedDocs(pLinkedDocs.filter((_, i) => i !== idx))}>
                        <X size={12} color="#94a3b8" />
                      </button>
                    </div>
                  ))}
                </div>
                {showDocDropdown && docSearch && (
                  <div style={styles.docDropdown}>
                    {filteredDocs.length === 0 ? (
                      <div style={{ padding: 12, fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>No matching documents</div>
                    ) : filteredDocs.slice(0, 20).map(d => (
                      <div key={d._id} style={styles.docItem}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}
                        onClick={() => { 
                          if (!pLinkedDocs.find(doc => doc.id === d._id)) {
                            setPLinkedDocs([...pLinkedDocs, { id: d._id, number: d.number, docType: d.type, customerName: d.customerName }]);
                          }
                          setDocSearch(''); 
                          setShowDocDropdown(false); 
                        }}>
                        <span>
                          <span style={styles.badge(d.type === 'invoice' ? 'blue' : 'green')}>
                            {d.type === 'invoice' ? 'INV' : 'CO'}
                          </span>
                          <span style={{ marginLeft: 8, fontWeight: 500 }}>{d.number}</span>
                        </span>
                        <span style={{ fontSize: 11, color: '#94a3b8' }}>{d.customerName}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button style={styles.btn('secondary')} onClick={() => setShowProjectModal(false)}>Cancel</button>
              <button style={styles.btn('primary')} onClick={handleCreateProject} disabled={saving}>
                {saving ? (
                  <><Loader2 size={14} className="animate-spin" /> {editingProject ? 'Saving…' : 'Creating…'}</>
                ) : (
                  <>{editingProject ? <Save size={14} /> : <Plus size={14} />} {editingProject ? 'Save Changes' : 'Create Project'}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Add Expense Modal ──────────────────────── */}
      {showExpenseModal && (
        <div style={styles.overlay} onClick={() => setShowExpenseModal(false)}>
          <div style={{ ...styles.modal, maxWidth: 900 }} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}><Receipt size={18} style={{ verticalAlign: -3, marginRight: 6 }} /> {editingItem ? 'Edit Expense' : 'Add Expense(s)'}</h2>
              <button onClick={() => setShowExpenseModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><X size={18} color="#94a3b8" /></button>
            </div>
            {projects.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center' }}>
                <FolderOpen size={48} style={{ color: '#cbd5e1', marginBottom: 16 }} />
                <p style={{ color: '#64748b', fontWeight: 600, marginBottom: 20 }}>No projects found. You need to create a project before adding expenses.</p>
                <button
                  style={styles.btn('primary')}
                  onClick={() => { setShowExpenseModal(false); openProjectModal(); }}
                >
                  <Plus size={14} /> Create New Project
                </button>
              </div>
            ) : (
              <div style={styles.modalBody}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20, marginBottom: 24 }}>
                  <div style={styles.field}>
                    <label style={styles.label}>Employee Name</label>
                    <input style={{ ...styles.input, background: '#f8fafc', color: '#64748b', cursor: 'not-allowed' }} value={user?.name || 'Unknown'} readOnly />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label}>Project *</label>
                    <FilterDropdown
                      value={eProjectId}
                      onChange={e => setEProjectId(e.target.value)}
                      options={[
                        { value: '', label: 'Select a project' },
                        ...projects.map(p => ({ value: p._id, label: p.projectName }))
                      ]}
                    />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label}>Date *</label>
                    <input style={styles.input} type="date" value={eDate} onChange={e => setEDate(e.target.value)} />
                  </div>
                </div>

                {editingItem ? (
                  // Single Edit View
                  <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr', gap: 12, marginBottom: 12 }}>
                    <div>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 800, color: '#475569', marginBottom: 6, textTransform: 'uppercase' }}>Amount *</label>
                      <input 
                        style={styles.input} 
                        type="number" step="0.01" 
                        value={eItems[0].amount} 
                        onChange={e => {
                          const newItems = [...eItems];
                          newItems[0].amount = e.target.value;
                          setEItems(newItems);
                        }} 
                        placeholder="0.00" 
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 800, color: '#475569', marginBottom: 6, textTransform: 'uppercase' }}>Description *</label>
                      <input 
                        style={styles.input} 
                        value={eItems[0].expenseDescription} 
                        onChange={e => {
                          const newItems = [...eItems];
                          newItems[0].expenseDescription = e.target.value;
                          setEItems(newItems);
                        }} 
                        placeholder="Purpose of expense..." 
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 800, color: '#475569', marginBottom: 6, textTransform: 'uppercase' }}>Notes</label>
                      <input 
                        style={styles.input} 
                        value={eItems[0].notes} 
                        onChange={e => {
                          const newItems = [...eItems];
                          newItems[0].notes = e.target.value;
                          setEItems(newItems);
                        }} 
                        placeholder="Add notes..." 
                      />
                    </div>
                  </div>
                ) : (
                  // Batch Add View
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr 140px 40px', gap: 12, marginBottom: 8, padding: '0 4px' }}>
                      <label style={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Amount *</label>
                      <label style={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Description *</label>
                      <label style={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Notes</label>
                      <label style={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Link Doc</label>
                      <span></span>
                    </div>

                    <div style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: 16 }}>
                      {eItems.map((item, idx) => (
                        <div key={idx} style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr 140px 40px', gap: 12, marginBottom: 12, alignItems: 'center' }}>
                          <input
                            style={styles.input}
                            type="number" step="0.01" value={item.amount}
                            placeholder="0.00"
                            onChange={e => {
                              const newItems = [...eItems];
                              newItems[idx].amount = e.target.value;
                              setEItems(newItems);
                            }}
                          />
                          <input
                            style={styles.input}
                            value={item.expenseDescription}
                            placeholder="Description..."
                            onChange={e => {
                              const newItems = [...eItems];
                              newItems[idx].expenseDescription = e.target.value;
                              setEItems(newItems);
                            }}
                          />
                          <input
                            style={styles.input}
                            value={item.notes}
                            placeholder="Optional notes"
                            onChange={e => {
                              const newItems = [...eItems];
                              newItems[idx].notes = e.target.value;
                              setEItems(newItems);
                            }}
                          />
                          <select
                            style={{ ...styles.input, padding: '8px' }}
                            value={item.linkedDocumentNumber || ''}
                            onChange={e => {
                              const newItems = [...eItems];
                              const doc = (projects.find(p => p._id === eProjectId)?.linkedDocuments || []).find(d => d.number === e.target.value);
                              newItems[idx].linkedDocumentNumber = e.target.value;
                              newItems[idx].linkedDocumentType = doc?.docType || null;
                              setEItems(newItems);
                            }}
                          >
                            <option value="">None</option>
                            {(projects.find(p => p._id === eProjectId)?.linkedDocuments || []).map((doc, i) => (
                              <option key={i} value={doc.number}>{doc.number}</option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => setEItems(eItems.filter((_, i) => i !== idx))}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', display: 'flex', justifyContent: 'center' }}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ))}
                    </div>

                    <button
                      type="button"
                      onClick={() => setEItems([...eItems, { amount: '', expenseDescription: '', notes: '', linkedDocumentNumber: '', linkedDocumentType: '' }])}
                      style={{ ...styles.btn('outline'), width: '100%', borderStyle: 'dashed', marginBottom: 24, justifyContent: 'center' }}
                    >
                      <Plus size={14} /> Add Row
                    </button>
                  </>
                )}

                <div style={styles.modalFooter}>
                  <button style={styles.btn('secondary')} onClick={() => setShowExpenseModal(false)}>Cancel</button>
                  <button style={styles.btn('primary')} onClick={handleCreateExpense} disabled={saving}>
                    {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : editingItem ? <><Edit size={14} /> Update Expense</> : <><Plus size={14} /> Add {eItems.filter(it => it.amount && it.expenseDescription).length || 0} Entries</>}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeExpenses;
